import React, { useMemo } from 'react';
import Card from '../components/ui/Card';
import { useAppContext } from '../contexts/AppContext';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { useProjectFilter } from '../hooks/useProjectFilter';

const Dvpr: React.FC = () => {
    const { highlightedItem, navigate, allData, functionColorMap, activeProject } = useAppContext();
    const { dvprTests, fmeaItems } = allData;
    const { isItemInProject } = useProjectFilter();

    const filteredTests = useMemo(() => {
        return dvprTests.filter(test => {
            // 1. Check if test has its own project metadata
            if (isItemInProject(test.projectName, test.epochId)) return true;

            // 2. Fallback: check linked risks
            if (!test.traceableRiskId || test.traceableRiskId.length === 0) return false;

            return test.traceableRiskId.some(riskId => {
                const risk = fmeaItems.find(f => f.id === riskId);
                return risk && isItemInProject(risk.projectName, risk.epochId);
            });
        });
    }, [dvprTests, fmeaItems, isItemInProject]);


    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">Design Verification Plan & Report (DVP&R)</h1>
                    <p className="text-text-secondary">Key validation tests mapped to specific risks with criteria.</p>
                </div>
                <div className="flex items-center gap-3">
                    <ModuleInfoModal
                        title="DVP&R Management"
                        description={`The Design Verification Plan & Report (DVP&R) links validation tests to FMEA risk items and customer requirements${activeProject !== 'all' ? ' for your selected project' : ''}. Each test validates that design controls effectively mitigate identified failure modes.`}
                        description2="DVP&R is the bridge between risk identification (FMEA) and risk validation (testing). Every critical failure mode should have at least one validation test with clear acceptance criteria and documented results."
                        benefits={[
                            `Complete traceability from customer requirements → FMEA risks → validation tests`,
                            `${filteredTests.length} validation tests covering system functions and failure modes`,
                            "Track test status: Pending, Pass, Fail for easy reporting",
                            "Color-coded function assignments for visual organization",
                            "Direct links to related FMEA items for context",
                            "Supports design review gates and validation milestones"
                        ]}
                        usageInstructions={[
                            "Review tests by function to ensure complete coverage across the system",
                            "Click Risk Item links to see the failure mode being validated by each test",
                            "Verify acceptance criteria are specific, measurable, and aligned with requirements",
                            "Update Test Results (Pass/Fail) as validation activities are completed",
                            "Use this view during design reviews to demonstrate risk mitigation",
                            "Identify gaps: high-risk FMEA items without corresponding validation tests"
                        ]}
                    />
                    <ActiveProjectBadge showStandard={false} />
                </div>
            </div>
            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-text-secondary">
                        <thead className="text-xs text-text-primary uppercase bg-background/50">
                            <tr>
                                <th scope="col" className="px-6 py-3">Test ID</th>
                                <th scope="col" className="px-6 py-3">Function</th>
                                <th scope="col" className="px-6 py-3">Risk Item Validated</th>
                                <th scope="col" className="px-6 py-3">Requirement Validated</th>
                                <th scope="col" className="px-6 py-3">Test Name</th>
                                <th scope="col" className="px-6 py-3">Procedure Summary</th>
                                <th scope="col" className="px-6 py-3">Acceptance Criteria</th>
                                <th scope="col" className="px-6 py-3">Actual Value</th>
                                <th scope="col" className="px-6 py-3">Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTests.map((test) => {
                                const relatedRisk = fmeaItems.find(f => test.traceableRiskId?.includes(f.id));
                                const isHighlighted = highlightedItem?.type === 'dvpr' && highlightedItem.id === test.testId;
                                return (
                                    <tr key={test.testId} className={`border-b border-border hover:bg-background ${isHighlighted ? 'animate-highlight' : ''}`}>
                                        <td className="px-6 py-4 font-medium text-text-primary">{test.testId}</td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-1 text-xs rounded" style={{ backgroundColor: `${functionColorMap[test.functionName]}20` }}>
                                                {test.functionName}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {relatedRisk && (
                                                <button onClick={() => navigate('/fmeas', { state: { type: 'fmea', id: relatedRisk.id } })} className="text-left text-primary hover:underline">
                                                    [{relatedRisk.id}] {relatedRisk.failureMode}
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {(allData.customerRequirements || []).filter(req =>
                                                (test.linkedCustomerReqIds || []).includes(req.id) ||
                                                (relatedRisk && (relatedRisk.linkedCustomerReqIds || []).includes(req.id))
                                            ).map(req => (
                                                <div key={req.id} className="text-xs text-text-secondary leading-tight mb-1">
                                                    <span className="font-bold">[{req.id}]</span> {req.requirementText}
                                                </div>
                                            ))}
                                        </td>
                                        <td className="px-6 py-4">{test.testName}</td>
                                        <td className="px-6 py-4">{test.procedure}</td>
                                        <td className="px-6 py-4">{test.criteria}</td>
                                        <td className="px-6 py-4">
                                            {test.testVariable && (
                                                <div className="text-[10px] text-text-secondary uppercase mb-0.5">{test.testVariable}</div>
                                            )}
                                            <span className="font-mono font-bold">{test.testValue || '--'}</span> {test.testUnit}
                                        </td>
                                        <td className="px-6 py-4">
                                            {test.testResult ? (
                                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${test.testResult === 'PASS' ? 'bg-emerald-100 text-emerald-700' :
                                                    test.testResult === 'FAIL' ? 'bg-red-100 text-red-700' :
                                                        'bg-amber-100 text-amber-700'
                                                    }`}>
                                                    {test.testResult}
                                                </span>
                                            ) : (
                                                <span className="text-text-secondary italic text-xs">Pending</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
};

export default Dvpr;
